import { useEffect, useState, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { Card, CardContent } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { SkeletonTeamGrid } from "../components/ui/skeleton"
import { getActiveYear } from "../utils/activeYear"
import { getScoringConfig, ScoringConfig } from "../utils/scoring"

interface Team {
  id: number
  name: string
  seed: number
  region: string
}

type Step = 'teams' | 'tiebreakers' | 'review'

export default function TeamPicker() {
  const [teams, setTeams] = useState<Team[]>([])
  const [selectedTeams, setSelectedTeams] = useState<number[]>([])
  const [entryName, setEntryName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set(['East', 'West', 'South', 'Midwest']))
  const [activeYear, setActiveYearState] = useState<number | null>(null)
  const [entryFee, setEntryFee] = useState<number>(20)
  const [entriesOpen, setEntriesOpen] = useState<boolean>(true)
  const [isEditing, setIsEditing] = useState(false)
  const [editEntryId, setEditEntryId] = useState<string | null>(null)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [animatingTeam, setAnimatingTeam] = useState<number | null>(null)
  const [counterPulse, setCounterPulse] = useState(false)
  const prevCountRef = useRef(0)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [scoringConfig, setScoringConfig] = useState<ScoringConfig[]>([])
  const [showScoringRef, setShowScoringRef] = useState(false)
  
  // Step management
  const [currentStep, setCurrentStep] = useState<Step>('teams')
  
  // Tiebreaker state (required for all entries)
  const [championTeamId, setChampionTeamId] = useState<number | null>(null)
  const [championshipPoints, setChampionshipPoints] = useState<string>('')
  const [championshipFouls, setChampionshipFouls] = useState<string>('')
  const [championSearch, setChampionSearch] = useState('')
  const [showChampionDropdown, setShowChampionDropdown] = useState(false)
  
  // Champion side pot (optional)
  const [enterChampionContest, setEnterChampionContest] = useState(false)
  const [championEntryFee, setChampionEntryFee] = useState<number>(5)
  const [championGameOpen, setChampionGameOpen] = useState<boolean>(false)

  useEffect(() => {
    async function loadTeams() {
      const year = await getActiveYear()
      setActiveYearState(year)

      // Load entry fee, entries_open, and champion game config from years table
      const { data: yearData } = await supabase
        .from('years')
        .select('entry_fee, entries_open, champion_entry_fee, champion_game_open')
        .eq('year', year)
        .single()
      
      if (yearData?.entry_fee) {
        setEntryFee(yearData.entry_fee)
      }
      setEntriesOpen(yearData?.entries_open ?? true)
      setChampionEntryFee(yearData?.champion_entry_fee ?? 5)
      setChampionGameOpen(yearData?.champion_game_open ?? false)
      
      // Query tournament_teams joined with team_schools to get names
      const { data, error } = await supabase
        .from('tournament_teams')
        .select('id, seed, region, school:team_schools(name)')
        .eq('year', year)
        .order('seed', { ascending: true })

      if (error) {
        setError('Failed to load teams')
      } else {
        // Transform data to flatten the school name
        const transformedTeams = (data || []).map((t: any) => ({
          id: t.id,
          name: t.school?.name || 'Unknown',
          seed: t.seed,
          region: t.region,
        }))
        setTeams(transformedTeams)
      }

      // Load scoring config
      const config = await getScoringConfig(year)
      setScoringConfig(config)

      // Check if we're editing an existing entry
      const editId = searchParams.get('edit')
      if (editId) {
        setIsEditing(true)
        setEditEntryId(editId)
        
        // Load existing entry data including tiebreakers
        const { data: entryData } = await supabase
          .from('entries')
          .select(`
            id, entry_name, champion_team_id, championship_total_points, championship_total_fouls,
            picks:entry_picks(team_id)
          `)
          .eq('id', editId)
          .single()
        
        if (entryData) {
          setEntryName(entryData.entry_name || '')
          const existingPicks = entryData.picks?.map((p: any) => p.team_id) || []
          setSelectedTeams(existingPicks)
          
          // Load tiebreaker data
          if (entryData.champion_team_id) {
            setChampionTeamId(entryData.champion_team_id)
          }
          if (entryData.championship_total_points !== null) {
            setChampionshipPoints(String(entryData.championship_total_points))
          }
          if (entryData.championship_total_fouls !== null) {
            setChampionshipFouls(String(entryData.championship_total_fouls))
          }
        }
        
        // Check if this entry is in the champion contest
        const { data: championEntry } = await supabase
          .from('champion_entries')
          .select('id')
          .eq('entry_id', editId)
          .maybeSingle()
        
        if (championEntry) {
          setEnterChampionContest(true)
        }
      }

      setLoading(false)
    }

    loadTeams()
  }, [searchParams])

  const toggleTeam = (teamId: number) => {
    // Trigger animation
    setAnimatingTeam(teamId)
    setTimeout(() => setAnimatingTeam(null), 200)
    
    if (selectedTeams.includes(teamId)) {
      setSelectedTeams(selectedTeams.filter(id => id !== teamId))
    } else if (selectedTeams.length < 16) {
      setSelectedTeams([...selectedTeams, teamId])
    }
    
    // Trigger counter pulse
    setCounterPulse(true)
    setTimeout(() => setCounterPulse(false), 250)
  }

  const toggleRegion = (region: string) => {
    const newExpanded = new Set(expandedRegions)
    if (newExpanded.has(region)) {
      newExpanded.delete(region)
    } else {
      newExpanded.add(region)
    }
    setExpandedRegions(newExpanded)
  }

  // Proceed to tiebreaker step
  const handleTeamsComplete = () => {
    if (selectedTeams.length !== 16) {
      setError('Please select exactly 16 teams')
      return
    }
    if (!entryName.trim()) {
      setError('Please enter an entry name')
      return
    }
    setError('')
    setCurrentStep('tiebreakers')
  }
  
  // Proceed from tiebreakers to review
  const handleTiebreakersComplete = () => {
    if (!championTeamId) {
      setError('Please select a champion')
      return
    }
    if (!championshipPoints.trim() || isNaN(parseInt(championshipPoints))) {
      setError('Please enter total points prediction')
      return
    }
    if (!championshipFouls.trim() || isNaN(parseInt(championshipFouls))) {
      setError('Please enter total fouls prediction')
      return
    }
    setError('')
    setShowReviewModal(true)
  }
  
  // Get champion team name for display
  const getChampionTeamName = () => {
    if (!championTeamId) return ''
    const team = teams.find(t => t.id === championTeamId)
    return team ? `(${team.seed}) ${team.name}` : ''
  }
  
  // Filter teams for champion search
  const filteredChampionTeams = teams.filter(t => 
    t.name.toLowerCase().includes(championSearch.toLowerCase()) ||
    String(t.seed).includes(championSearch)
  ).sort((a, b) => a.seed - b.seed)

  // Actual submission after review confirmation
  const handleConfirmSubmit = async () => {
    // Set saving state FIRST to disable buttons and prevent double-clicks
    if (saving) return // Extra guard against double-clicks
    setSaving(true)
    setError('')

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setError('You must be logged in')
      setSaving(false)
      return
    }

    // Check if entry name is already taken (for this year)
    const { data: existingEntry } = await supabase
      .from('entries')
      .select('id')
      .eq('year', activeYear)
      .ilike('entry_name', entryName.trim())
      .maybeSingle()

    // If editing, allow keeping the same name for this entry
    if (existingEntry && (!isEditing || existingEntry.id !== editEntryId)) {
      setError('This entry name is already taken. Please choose another.')
      setSaving(false)
      return
    }

    // Handle edit mode
    if (isEditing && editEntryId) {
      // Update entry with tiebreakers
      const { error: updateError } = await supabase
        .from('entries')
        .update({ 
          entry_name: entryName.trim(),
          champion_team_id: championTeamId,
          championship_total_points: parseInt(championshipPoints),
          championship_total_fouls: parseInt(championshipFouls)
        })
        .eq('id', editEntryId)

      if (updateError) {
        setError(updateError.message)
        setSaving(false)
        return
      }

      // Delete old picks
      const { error: deleteError } = await supabase
        .from('entry_picks')
        .delete()
        .eq('entry_id', editEntryId)

      if (deleteError) {
        setError(deleteError.message)
        setSaving(false)
        return
      }

      // Insert new picks
      const picks = selectedTeams.map(teamId => ({
        entry_id: editEntryId,
        team_id: teamId
      }))

      const { error: picksError } = await supabase
        .from('entry_picks')
        .insert(picks)

      if (picksError) {
        setError(picksError.message)
        setSaving(false)
        return
      }
      
      // Handle champion contest entry
      if (enterChampionContest && championGameOpen) {
        // Check if already in champion contest
        const { data: existingChampEntry } = await supabase
          .from('champion_entries')
          .select('id')
          .eq('entry_id', editEntryId)
          .maybeSingle()
        
        if (!existingChampEntry) {
          // Add to champion contest
          const { error: champError } = await supabase
            .from('champion_entries')
            .insert({
              entry_id: editEntryId,
              user_id: user.id,
              year: activeYear,
              is_paid: false
            })
          
          if (champError) {
            console.error('Champion entry error:', champError)
          }
        }
      } else {
        // Remove from champion contest if unchecked
        await supabase
          .from('champion_entries')
          .delete()
          .eq('entry_id', editEntryId)
      }

      setSaving(false)
      setShowReviewModal(false)
      setShowSuccessModal(true)
      return
    }

    // Handle create mode with tiebreakers
    const { data: entry, error: entryError } = await supabase
      .from('entries')
      .insert({
        user_id: user.id,
        entry_name: entryName.trim(),
        year: activeYear,
        is_paid: false,
        points: 0,
        champion_team_id: championTeamId,
        championship_total_points: parseInt(championshipPoints),
        championship_total_fouls: parseInt(championshipFouls)
      })
      .select()
      .single()

    if (entryError) {
      setError(entryError.message)
      setSaving(false)
      return
    }

    // entry_picks.team_id now references tournament_teams.id
    const picks = selectedTeams.map(teamId => ({
      entry_id: entry.id,
      team_id: teamId
    }))

    const { error: picksError } = await supabase
      .from('entry_picks')
      .insert(picks)

    if (picksError) {
      setError(picksError.message)
      setSaving(false)
      return
    }
    
    // Add to champion contest if opted in
    if (enterChampionContest && championGameOpen) {
      const { error: champError } = await supabase
        .from('champion_entries')
        .insert({
          entry_id: entry.id,
          user_id: user.id,
          year: activeYear,
          is_paid: false
        })
      
      if (champError) {
        console.error('Champion entry error:', champError)
      }
    }

    setSaving(false)
    setShowReviewModal(false)
    setShowSuccessModal(true)
  }

  // Get selected team objects for review modal
  const getSelectedTeamObjects = () => {
    return teams.filter(t => selectedTeams.includes(t.id))
  }

  const regions = ['East', 'West', 'South', 'Midwest']
  const groupedTeams = teams.reduce((acc, team) => {
    if (!acc[team.region]) acc[team.region] = []
    acc[team.region].push(team)
    return acc
  }, {} as Record<string, Team[]>)

  if (loading) {
    return (
      <div className="space-y-4 animate-fade-in">
        {/* Skeleton Header */}
        <div className="sticky top-16 z-20 bg-[#F5F5F5] pt-2 pb-4 -mx-4 px-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="skeleton h-10 flex-1" />
            <div className="skeleton h-10 w-16 rounded-lg" />
          </div>
        </div>
        
        {/* Skeleton Title */}
        <div>
          <div className="skeleton h-6 w-48 mb-2" />
          <div className="skeleton h-4 w-64" />
        </div>
        
        {/* Skeleton Regions */}
        {['East', 'West', 'South', 'Midwest'].map(region => (
          <Card key={region} className="bg-white border-0 shadow-sm overflow-hidden">
            <div className="flex items-center justify-between p-4">
              <div className="skeleton h-5 w-20" />
              <div className="skeleton h-5 w-5 rounded-full" />
            </div>
            <CardContent className="pt-0 pb-4">
              <SkeletonTeamGrid />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  // Show message if entries are closed
  if (!entriesOpen) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h2 className="text-xl font-bold text-[#1e3a5f] mb-2">Entries Are Closed</h2>
        <p className="text-gray-500 mb-6 max-w-md">
          Entry submissions for Wright Picks {activeYear} are currently closed. 
          Check back later or contact the pool administrator.
        </p>
        <a 
          href="/dashboard" 
          className="px-6 py-3 bg-[#1e3a5f] text-white rounded-lg font-medium hover:bg-[#002a6b]"
        >
          Back to Dashboard
        </a>
      </div>
    )
  }

  // Helper to render tiebreakers content
  const renderTiebreakersStep = () => (
    <div className="space-y-4 pb-24" onClick={() => setShowChampionDropdown(false)}>
      {/* Header with back button */}
      <div className="flex items-center gap-3 pt-2">
        <button
          onClick={() => setCurrentStep('teams')}
          className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-gray-50 border border-gray-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1e3a5f" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-[#1e3a5f]">Tiebreakers & Side Pot</h1>
          <p className="text-sm text-gray-500">{entryName}</p>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-center text-sm">
          {error}
        </div>
      )}
      
      {/* Section: Required Tiebreakers */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-xs font-bold">1</div>
          <h2 className="text-sm font-semibold text-[#1e3a5f] uppercase tracking-wide">Main Pool Tiebreakers</h2>
          <span className="text-xs text-red-500 font-medium">(Required)</span>
        </div>
        
        {/* Champion Picker */}
        <Card className="bg-white border-0 shadow-sm mb-3">
          <CardContent className="pt-4 pb-4">
            <label className="block text-sm font-medium text-[#1e3a5f] mb-2">
              National Champion Pick
            </label>
            <div className="relative" onClick={(e) => e.stopPropagation()}>
              <div className="relative">
                <input
                  type="text"
                  value={championTeamId ? getChampionTeamName() : championSearch}
                  onChange={(e) => {
                    setChampionSearch(e.target.value)
                    setChampionTeamId(null)
                    setShowChampionDropdown(true)
                  }}
                  onFocus={() => setShowChampionDropdown(true)}
                  placeholder="Search or tap to select..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/10 bg-gray-50 focus:bg-white transition-colors"
                />
                {championTeamId ? (
                  <button
                    onClick={() => {
                      setChampionTeamId(null)
                      setChampionSearch('')
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                )}
              </div>
              
              {showChampionDropdown && !championTeamId && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-50">
                  <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                    {['East', 'West', 'South', 'Midwest'].map(region => {
                      const regionTeams = filteredChampionTeams.filter(t => t.region === region)
                      if (regionTeams.length === 0) return null
                      return (
                        <div key={region}>
                          <div className="px-3 py-1.5 bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-semibold sticky top-0">
                            {region}
                          </div>
                          {regionTeams.map(team => (
                            <button
                              key={team.id}
                              onClick={() => {
                                setChampionTeamId(team.id)
                                setChampionSearch('')
                                setShowChampionDropdown(false)
                              }}
                              className="w-full text-left px-3 py-2.5 hover:bg-gray-50 flex items-center gap-3 text-sm transition-colors"
                            >
                              <span className="w-2 h-2 rounded-full bg-green-500" />
                              <span className="w-6 text-sm font-semibold text-[#1e3a5f]">{team.seed}</span>
                              <span className="text-gray-800 font-medium">{team.name}</span>
                            </button>
                          ))}
                        </div>
                      )
                    })}
                    {filteredChampionTeams.length === 0 && (
                      <div className="px-4 py-6 text-sm text-gray-500 text-center">No teams found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Points and Fouls in one card */}
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1">
                  Total Points
                </label>
                <p className="text-[11px] text-gray-400 mb-2">Combined score</p>
                <input
                  type="number"
                  inputMode="numeric"
                  value={championshipPoints}
                  onChange={(e) => setChampionshipPoints(e.target.value)}
                  placeholder="e.g. 145"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/10 bg-gray-50 focus:bg-white transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#1e3a5f] mb-1">
                  Total Fouls
                </label>
                <p className="text-[11px] text-gray-400 mb-2">Combined fouls</p>
                <input
                  type="number"
                  inputMode="numeric"
                  value={championshipFouls}
                  onChange={(e) => setChampionshipFouls(e.target.value)}
                  placeholder="e.g. 38"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/10 bg-gray-50 focus:bg-white transition-colors"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Section: Optional Side Pot */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-6 h-6 rounded-full ${championGameOpen ? 'bg-[#F15A22]' : 'bg-gray-300'} text-white flex items-center justify-center text-xs font-bold`}>2</div>
          <h2 className="text-sm font-semibold text-[#1e3a5f] uppercase tracking-wide">Champion Side Pot</h2>
          <span className="text-xs text-gray-400 font-medium">(Optional - ${championEntryFee})</span>
        </div>
        
        {championGameOpen ? (
          <Card className={`border-2 transition-all ${enterChampionContest ? 'border-[#F15A22] bg-[#F15A22]/5' : 'border-gray-200 bg-white'} shadow-sm`}>
            <CardContent className="pt-4 pb-4">
              <label className="flex items-start gap-4 cursor-pointer">
                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0 mt-0.5 ${
                  enterChampionContest 
                    ? 'bg-[#F15A22] border-[#F15A22]' 
                    : 'border-gray-300 bg-white'
                }`}>
                  {enterChampionContest && (
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={enterChampionContest}
                  onChange={(e) => setEnterChampionContest(e.target.checked)}
                  className="sr-only"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[#1e3a5f]">Enter Side Pot</span>
                    <span className="text-lg font-bold text-[#F15A22]">+${championEntryFee}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Separate contest using your tiebreaker picks above. Winner(s) closest to actual championship results split the pot.
                  </p>
                  {enterChampionContest && (
                    <div className="mt-3 pt-3 border-t border-[#F15A22]/20">
                      <div className="flex items-center gap-2 text-sm text-[#F15A22]">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                          <polyline points="22 4 12 14.01 9 11.01"/>
                        </svg>
                        <span className="font-medium">You're in! Pay ${championEntryFee} with your entry.</span>
                      </div>
                    </div>
                  )}
                </div>
              </label>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-2 border-gray-200 bg-gray-50 shadow-sm opacity-60">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-gray-500">Side Pot Not Available</p>
                  <p className="text-sm text-gray-400">The champion side pot is currently closed.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg z-30">
        <Button
          onClick={handleTiebreakersComplete}
          disabled={!championTeamId || !championshipPoints.trim() || !championshipFouls.trim()}
          className="w-full h-12 bg-[#1e3a5f] hover:bg-[#002a6b] text-white font-semibold disabled:opacity-50"
        >
          Review Entry →
        </Button>
      </div>
    </div>
  )

  // Helper to render teams step
  const renderTeamsStep = () => (
    <div className="space-y-4 pb-24">
      {/* Sticky Header with Entry Name and Counter */}
      <div className="sticky top-16 z-20 bg-[#F5F5F5] pt-2 pb-4 -mx-4 px-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <Input
            placeholder="Entry name (e.g., Upset Special)"
            value={entryName}
            onChange={(e) => setEntryName(e.target.value.slice(0, 20))}
            maxLength={20}
            className="flex-1 h-10 border-gray-300 bg-white"
          />
          <div className={`px-3 py-2 rounded-lg font-bold text-sm whitespace-nowrap transition-all ${
            selectedTeams.length === 16 
              ? 'bg-green-100 text-green-700' 
              : 'bg-[#F15A22]/10 text-[#F15A22]'
          } ${counterPulse ? 'animate-counter-pulse' : ''}`}>
            {selectedTeams.length}/16
          </div>
        </div>
        
        {error && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded-lg p-2 text-red-700 text-center text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Page Title */}
      <div>
        <h1 className="text-xl font-bold text-[#1e3a5f]">
          {isEditing ? 'Edit Your Entry' : 'Pick Your 16 Teams'}
        </h1>
        <p className="text-gray-500 text-sm">
          {isEditing ? 'Update your team selections' : 'Tap to select teams from each region'}
        </p>
      </div>

      {/* Scoring Quick Reference */}
      <button
        onClick={() => setShowScoringRef(!showScoringRef)}
        className="w-full flex items-center justify-between p-3 bg-white rounded-lg shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#F15A22" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 16v-4"/>
            <path d="M12 8h.01"/>
          </svg>
          <span className="text-sm font-medium text-[#1e3a5f]">Scoring Reference</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">Seed × Multiplier = Points</span>
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            className={`text-gray-400 transition-transform ${showScoringRef ? 'rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </button>

      {showScoringRef && scoringConfig.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden animate-fade-in">
          <div className="divide-y divide-gray-50">
            {scoringConfig.map(config => (
              <div key={config.round} className="flex items-center justify-between px-3 py-2">
                <span className="text-xs text-gray-600">{config.round_name}</span>
                <span className="text-xs font-bold text-[#F15A22]">{config.multiplier}×</span>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 px-3 py-2 text-center">
            <span className="text-[10px] text-gray-400 uppercase tracking-wide">
              Example: 6 seed wins Sweet 16 = 6 × 2 = 12 pts
            </span>
          </div>
        </div>
      )}

      {/* Collapsible Regions */}
      {regions.map(region => {
        const regionTeams = groupedTeams[region] || []
        const selectedCount = regionTeams.filter(t => selectedTeams.includes(t.id)).length
        const isExpanded = expandedRegions.has(region)

        return (
          <Card key={region} className="bg-white border-0 shadow-sm overflow-hidden">
            {/* Region Header (clickable) */}
            <button
              onClick={() => toggleRegion(region)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="font-semibold text-[#1e3a5f]">{region}</span>
                {selectedCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#F15A22] text-white">
                    {selectedCount}
                  </span>
                )}
              </div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
                className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>

            {/* Teams Grid (collapsible) */}
            {isExpanded && (
              <CardContent className="pt-0 pb-4">
                <div className="grid grid-cols-2 gap-2">
                  {regionTeams.sort((a, b) => a.seed - b.seed).map(team => {
                    const isSelected = selectedTeams.includes(team.id)
                    const isDisabled = !isSelected && selectedTeams.length >= 16
                    
                    return (
                      <button
                        key={team.id}
                        onClick={() => toggleTeam(team.id)}
                        disabled={isDisabled}
                        className={`p-2.5 rounded-lg border transition-all text-left ${
                          isSelected
                            ? 'bg-[#F15A22]/10 border-[#F15A22] ring-selected'
                            : isDisabled
                            ? 'bg-gray-50 border-gray-100 opacity-50'
                            : 'bg-white border-gray-200 hover:border-[#1e3a5f]'
                        } ${animatingTeam === team.id ? 'animate-team-select' : ''}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            isSelected ? 'bg-[#F15A22] text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {team.seed}
                          </span>
                          <span className={`text-sm font-medium truncate ${
                            isSelected ? 'text-[#1e3a5f]' : isDisabled ? 'text-gray-400' : 'text-gray-700'
                          }`}>
                            {team.name}
                          </span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </CardContent>
            )}
          </Card>
        )
      })}

      {/* Sticky Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 shadow-lg z-30">
        <Button
          onClick={handleTeamsComplete}
          disabled={selectedTeams.length !== 16 || !entryName.trim() || saving}
          className="w-full h-12 bg-[#1e3a5f] hover:bg-[#002a6b] text-white font-semibold disabled:opacity-50"
        >
          {selectedTeams.length === 16 ? 'Continue to Tiebreakers →' : `Select ${16 - selectedTeams.length} more`}
        </Button>
      </div>
    </div>
  )

  // Main return - conditionally render step content, always render modals
  return (
    <>
      {currentStep === 'teams' && renderTeamsStep()}
      {currentStep === 'tiebreakers' && renderTiebreakersStep()}

      {/* Review Modal - Always available */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4" onClick={() => setShowReviewModal(false)}>
          <div className="bg-white w-full max-w-sm max-h-[85vh] overflow-hidden rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="p-4 border-b border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <div>
                    <div className="font-bold text-[#1e3a5f] text-lg">{entryName}</div>
                    <div className="text-sm text-gray-500">16 teams selected</div>
                  </div>
                </div>
                <button
                  onClick={() => setShowReviewModal(false)}
                  disabled={saving}
                  className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Tiebreaker Summary */}
            <div className="px-4 py-3 bg-gradient-to-r from-[#1e3a5f]/5 to-[#F15A22]/5 border-b border-gray-100 flex-shrink-0">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-2">Tiebreakers</div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Champion:</span>
                  <span className="font-semibold text-[#1e3a5f]">{getChampionTeamName()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Total Points:</span>
                  <span className="font-semibold text-[#1e3a5f]">{championshipPoints}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Total Fouls:</span>
                  <span className="font-semibold text-[#1e3a5f]">{championshipFouls}</span>
                </div>
              </div>
              {enterChampionContest && championGameOpen && (
                <div className="mt-3 pt-2 border-t border-[#F15A22]/20 text-sm text-[#F15A22] font-medium flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  Champion Side Pot (+${championEntryFee})
                </div>
              )}
            </div>

            {/* Teams List - Scrollable */}
            <div className="overflow-y-auto flex-1 min-h-0">
              {getSelectedTeamObjects()
                .sort((a, b) => {
                  const regionOrder = ['East', 'West', 'South', 'Midwest']
                  const regionDiff = regionOrder.indexOf(a.region) - regionOrder.indexOf(b.region)
                  if (regionDiff !== 0) return regionDiff
                  return a.seed - b.seed
                })
                .map((team, index, arr) => {
                  const showRegion = index === 0 || arr[index - 1].region !== team.region
                  return (
                    <div key={team.id}>
                      {showRegion && (
                        <div className="px-4 py-2 bg-gray-50 text-[10px] uppercase tracking-wider text-gray-500 font-semibold sticky top-0 border-b border-gray-100">
                          {team.region}
                        </div>
                      )}
                      <div className="flex items-center px-4 py-2.5 border-b border-gray-50">
                        <span className="w-7 text-sm text-gray-400 font-medium">{team.seed}</span>
                        <span className="flex-1 text-sm text-gray-700 font-medium">{team.name}</span>
                      </div>
                    </div>
                  )
                })}
            </div>

            {/* Modal Footer - Price and Buttons */}
            <div className="p-4 border-t bg-gray-50 flex-shrink-0">
              {!isEditing && (
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-600">Total to pay:</span>
                  <span className="text-xl font-bold text-[#F15A22]">
                    ${entryFee + (enterChampionContest && championGameOpen ? championEntryFee : 0)}
                  </span>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowReviewModal(false)
                    setCurrentStep('tiebreakers')
                  }}
                  disabled={saving}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-600 text-sm font-medium hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  disabled={saving}
                  className="flex-[2] px-4 py-3 bg-[#1e3a5f] text-white rounded-xl text-sm font-semibold hover:bg-[#002a6b] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Submit Entry'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal - Always available */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white w-full max-w-sm overflow-hidden rounded-xl shadow-2xl text-center">
            {/* Success Icon */}
            <div className="pt-8 pb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-success-pop">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <h2 className="text-xl font-bold text-[#1e3a5f]">
                {isEditing ? 'Entry Updated!' : 'Entry Created!'}
              </h2>
              <p className="text-gray-500 mt-2 px-4">
                {isEditing 
                  ? `"${entryName}" has been updated successfully.`
                  : `"${entryName}" has been submitted. Don't forget to pay $${entryFee}${enterChampionContest && championGameOpen ? ` + $${championEntryFee} for the side pot` : ''}!`
                }
              </p>
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t bg-gray-50 space-y-2">
              <button
                onClick={() => navigate('/my-entries')}
                className="w-full px-4 py-3 bg-[#1e3a5f] text-white rounded-lg font-medium hover:bg-[#002a6b]"
              >
                View My Entries
              </button>
              {!isEditing && (
                <button
                  onClick={() => {
                    setShowSuccessModal(false)
                    setSelectedTeams([])
                    setEntryName('')
                    setCurrentStep('teams')
                  }}
                  className="w-full px-4 py-2 text-[#1e3a5f] font-medium hover:underline"
                >
                  Create Another Entry
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
